import { uiText } from '../utils/i18n';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from '../store';
import { LANGUAGE_OPTIONS, useLang, useT } from '../utils/i18n';
import type { Lang } from '../utils/i18n';
import styles from './SettingsPage.module.css';

type SettingsTab = 'general' | 'billing' | 'appearance' | 'auth' | 'guide' | 'about';

export function SettingsPage() {
  const t = useT();
  const [activeTab, setActiveTab] = useState<SettingsTab>('general');
  const token = useStore((s) => s.token);
  const logout = useStore((s) => s.logout);
  const clearSessions = useStore((s) => s.clearSessions);

  const TABS: { id: SettingsTab; label: string }[] = [
    { id: 'general', label: t('settings.general') },
    { id: 'billing', label: '额度与账单' },
    { id: 'appearance', label: t('settings.appearance') },
    { id: 'auth', label: t('settings.auth') },
    { id: 'guide', label: t('settings.guide') },
    { id: 'about', label: t('settings.about') },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.nav}>
        <h2 className={styles.navTitle}>{t('settings.title')}</h2>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.navItem} ${activeTab === tab.id ? styles.navItemActive : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className={styles.content}>
        {activeTab === 'general' && <GeneralTab onClear={clearSessions} />}
        {activeTab === 'billing' && <BillingTab token={token} />}
        {activeTab === 'appearance' && <AppearanceTab />}
        {activeTab === 'auth' && <AuthTab token={token} onLogout={logout} />}
        {activeTab === 'guide' && <GuideTab />}
        {activeTab === 'about' && <AboutTab />}
      </div>
    </div>
  );
}

interface BillingPlan {
  id: string;
  name: string;
  priceUsd: number;
  monthlyPoints: number;
  recommended?: boolean;
}

interface CreditPack {
  id: string;
  priceUsd: number;
  points: number;
}

interface BillingSummary {
  workspaceName: string;
  role: 'owner' | 'admin' | 'member';
  canPurchase: boolean;
  balancePoints: number;
  totalGrantedPoints: number;
  totalUsedPoints: number;
  usedLast30Days: number;
  freeGrantPoints: number;
  plans: BillingPlan[];
  creditPacks: CreditPack[];
}

interface BillingUsage {
  id: string;
  userName: string;
  botName?: string;
  model?: string;
  chargedPoints: number;
  createdAt: number;
}

const formatPoints = (points: number) => new Intl.NumberFormat('zh-CN').format(points);

function BillingTab({ token }: { token: string | null }) {
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [usage, setUsage] = useState<BillingUsage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const activeView = useStore((s) => s.activeView);
  const hasLoadedRef = useRef(false);
  const requestIdRef = useRef(0);
  const lastRefreshAtRef = useRef(0);

  const refreshBilling = useCallback(async (force = false) => {
    if (!token) {
      setLoading(false);
      setError('登录状态已失效，请重新登录');
      return;
    }
    const now = Date.now();
    if (!force && now - lastRefreshAtRef.current < 1_000) return;
    lastRefreshAtRef.current = now;
    const requestId = ++requestIdRef.current;
    const headers = { Authorization: `Bearer ${token}` };
    if (!hasLoadedRef.current) setLoading(true);

    try {
      const [summaryData, usageData] = await Promise.all([
        fetch('/api/billing/summary', { headers, cache: 'no-store' }).then(async (res) => {
          if (!res.ok) throw new Error((await res.json()).error || '额度信息加载失败');
          return res.json() as Promise<BillingSummary>;
        }),
        fetch('/api/billing/usage?limit=20', { headers, cache: 'no-store' }).then(async (res) => {
          if (!res.ok) throw new Error((await res.json()).error || '用量明细加载失败');
          return res.json() as Promise<{ usage: BillingUsage[] }>;
        }),
      ]);
      if (requestId !== requestIdRef.current) return;
      setSummary(summaryData);
      setUsage(usageData.usage || []);
      setError('');
      hasLoadedRef.current = true;
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(err instanceof Error ? err.message : '额度信息加载失败');
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (activeView !== 'settings') return;
    void refreshBilling();

    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void refreshBilling();
    };
    window.addEventListener('focus', refreshWhenVisible);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    const intervalId = window.setInterval(refreshWhenVisible, 30_000);

    return () => {
      window.removeEventListener('focus', refreshWhenVisible);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      window.clearInterval(intervalId);
    };
  }, [activeView, refreshBilling]);

  if (loading) return <div className={styles.billingState}>{uiText("正在加载组织额度…")}</div>;
  if (error || !summary) return <div className={styles.billingState}>{error || uiText("暂时无法读取额度信息")}</div>;

  const usedRatio = summary.totalGrantedPoints > 0
    ? Math.min(100, (summary.totalUsedPoints / summary.totalGrantedPoints) * 100)
    : 0;
  const purchaseLabel = summary.canPurchase ? '待上线' : '仅所有者可购买';

  return (
    <div className={styles.billingContent}>
      <div className={styles.billingHeaderRow}>
        <div>
          <span className={styles.sectionEyebrow}>CREDITS & BILLING</span>
          <h2 className={styles.tabTitle}>{uiText("额度与账单")}</h2>
          <p className={styles.tabDesc}>{uiText("额度由组织成员共享，所有消费统一从当前组织扣除。")}</p>
        </div>
        <div className={styles.comingSoonBadge}>{uiText("待上线")}</div>
      </div>

      <div className={styles.billingNotice}>
        <div className={styles.noticeIcon}>⌁</div>
        <div>
          <strong>{uiText("支付功能正在准备中")}</strong>
          <span>{uiText("套餐与额度已经可以查看，支付渠道确定后即可开放购买。")}</span>
        </div>
      </div>

      <section className={styles.balanceCard}>
        <div className={styles.balanceTopline}>
          <span>{summary.workspaceName}</span>
          <span>{summary.role === 'owner' ? uiText("组织所有者") : uiText("组织共享额度")}</span>
        </div>
        <div className={styles.balanceMain}>
          <div>
            <div className={styles.balanceLabel}>{uiText("可用 Nova 点")}</div>
            <div className={styles.balanceValue}>{formatPoints(summary.balancePoints)}</div>
          </div>
          <div className={styles.balanceStats}>
            <div><span>{uiText("免费赠送")}</span><strong>{formatPoints(summary.freeGrantPoints)}</strong></div>
            <div><span>{uiText("近 30 天消耗")}</span><strong>{formatPoints(summary.usedLast30Days)}</strong></div>
            <div><span>{uiText("累计消耗")}</span><strong>{formatPoints(summary.totalUsedPoints)}</strong></div>
          </div>
        </div>
        <div className={styles.balanceProgress}><span style={{ width: `${usedRatio}%` }} /></div>
        <div className={styles.balanceFootnote}>{uiText("免费额度为组织一次性额度，所有成员共同使用。当前按 $1 = 750 Nova 点折算。")}</div>
      </section>

      <section className={styles.billingSection}>
        <div className={styles.billingSectionHead}>
          <div><h3>{uiText("月度套餐")}</h3><p>{uiText("套餐之间仅额度不同，所有产品能力保持一致。")}</p></div>
        </div>
        <div className={styles.planGrid}>
          {summary.plans.map((plan) => (
            <article key={plan.id} className={`${styles.planCard} ${plan.recommended ? styles.planCardRecommended : ''}`}>
              {plan.recommended && <span className={styles.recommendedTag}>{uiText("推荐")}</span>}
              <h4>{plan.name}</h4>
              <div className={styles.planPrice}><span>$</span>{formatPoints(plan.priceUsd)}<small>{uiText("/ 月")}</small></div>
              <div className={styles.planPoints}>{formatPoints(plan.monthlyPoints)} <span>{uiText("Nova 点")}</span></div>
              <p>{uiText("组织内所有成员共享使用")}</p>
              <button className={styles.billingDisabledBtn} disabled>{purchaseLabel}</button>
            </article>
          ))}
        </div>
      </section>

      <section className={styles.billingSection}>
        <div className={styles.billingSectionHead}>
          <div><h3>{uiText("额外额度")}</h3><p>{uiText("月度额度不足时，可由组织所有者补充。")}</p></div>
        </div>
        <div className={styles.creditPackGrid}>
          {summary.creditPacks.map((pack) => (
            <div key={pack.id} className={styles.creditPackCard}>
              <div><strong>{formatPoints(pack.points)}</strong><span>{uiText("Nova 点")}</span></div>
              <div className={styles.creditPackPrice}>${formatPoints(pack.priceUsd)}</div>
              <button className={styles.billingDisabledBtn} disabled>{purchaseLabel}</button>
            </div>
          ))}
        </div>
        {!summary.canPurchase && <p className={styles.ownerOnlyHint}>{uiText("只有组织所有者可以购买套餐或充值额度。")}</p>}
      </section>

      <section className={styles.billingSection}>
        <div className={styles.billingSectionHead}>
          <div><h3>{uiText("最近用量")}</h3><p>{uiText("组织成员和自动任务的消费都会记录在这里。")}</p></div>
        </div>
        {usage.length === 0 ? (
          <div className={styles.emptyUsage}>{uiText("还没有产生额度消费")}</div>
        ) : (
          <div className={styles.usageTable}>
            <div className={styles.usageTableHead}>
              <span>{uiText("时间")}</span><span>{uiText("成员 / Agent")}</span><span>{uiText("模型")}</span><span>{uiText("消耗")}</span>
            </div>
            {usage.map((event) => (
              <div key={event.id} className={styles.usageTableRow}>
                <span>{new Date(event.createdAt * 1000).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                <span><strong>{event.userName}</strong><small>{event.botName || uiText("系统任务")}</small></span>
                <span>{event.model || '—'}</span>
                <span>-{formatPoints(event.chargedPoints)} {uiText("点")}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function ConfirmModal({ title, desc, confirmText, onConfirm, onCancel }: {
  title: string; desc: string; confirmText: string;
  onConfirm: () => void; onCancel: () => void;
}) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.6)', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
    }} onClick={onCancel}>
      <div style={{
        background: '#2a2a22', borderRadius: 16, padding: '32px 28px',
        maxWidth: 380, width: '90%', textAlign: 'center',
        border: '1px solid rgba(255,255,255,0.12)',
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
        <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: '#f1ecdf' }}>{title}</h3>
        <p style={{ fontSize: 13, color: '#b0a890', marginBottom: 24, lineHeight: 1.6 }}>{desc}</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button onClick={onCancel} style={{
            padding: '10px 24px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)',
            background: 'transparent', color: '#f1ecdf', cursor: 'pointer', fontSize: 14, fontWeight: 500,
          }}>{uiText("取消")}</button>
          <button onClick={onConfirm} style={{
            padding: '10px 24px', borderRadius: 8, border: 'none',
            background: '#e34a2a', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600,
          }}>{confirmText}</button>
        </div>
      </div>
    </div>
  );
}

interface OrgInfo {
  id: string;
  name: string;
  role?: string;
  createdAt?: string;
}

function GeneralTab({ onClear }: { onClear: () => void }) {
  const t = useT();
  const [lang, setLang] = useLang();
  const [confirmClear, setConfirmClear] = useState(false);

  const handleLangChange = (newLang: Lang) => {
    setLang(newLang);
  };

  return (
    <div className={styles.tabContent}>
      <span className={styles.sectionEyebrow}>{t('general.eyebrow')}</span>
      <h2 className={styles.tabTitle}>{t('general.title')}</h2>
      <p className={styles.tabDesc}>{t('general.desc')}</p>

      <div className={styles.settingRow}>
        <div className={styles.settingLabel}>{t('general.language')}</div>
        <div className={styles.langRow}>
          {LANGUAGE_OPTIONS.map((option) => (
            <button
              key={option.code}
              className={`${styles.langBtn} ${lang === option.code ? styles.langBtnActive : ''}`}
              onClick={() => handleLangChange(option.code)}
              lang={option.htmlLang}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('general.chatHistory')}</h3>
        <p className={styles.sectionDesc}>{t('general.chatHistoryDesc')}</p>
        <button className={styles.dangerBtn} onClick={() => setConfirmClear(true)}>
          {t('general.clearSessions')}
        </button>
      </div>

      {confirmClear && (
        <ConfirmModal
          title={uiText("确定清除所有会话？")}
          desc={uiText("清除后所有聊天记录将被删除，无法恢复。")}
          confirmText={uiText("确认清除")}
          onConfirm={() => { onClear(); setConfirmClear(false); }}
          onCancel={() => setConfirmClear(false)}
        />
      )}
    </div>
  );
}

type MsgFontSize = 'small' | 'medium' | 'large';
const MSG_FONT_SIZES: { id: MsgFontSize; label: string; value: string }[] = [
  { id: 'small', label: '小', value: '13px' },
  { id: 'medium', label: '中', value: '14px' },
  { id: 'large', label: '大', value: '15.5px' },
];

function AppearanceTab() {
  const [fontSize, setFontSize] = useState<MsgFontSize>(() => {
    const saved = localStorage.getItem('novax:msgFontSize');
    if (saved === '13px') return 'small';
    if (saved === '15.5px') return 'large';
    return 'medium';
  });

  const handleFontSize = (size: MsgFontSize) => {
    setFontSize(size);
    const entry = MSG_FONT_SIZES.find((s) => s.id === size)!;
    document.documentElement.style.setProperty('--msg-font-size', entry.value);
    localStorage.setItem('novax:msgFontSize', entry.value);
  };

  return (
    <div className={styles.tabContent}>
      <span className={styles.sectionEyebrow}>APPEARANCE</span>
      <h2 className={styles.tabTitle}>{uiText("外观")}</h2>
      <p className={styles.tabDesc}>{uiText("自定义界面显示偏好")}</p>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>{uiText("消息字体大小")}</h3>
        <p className={styles.sectionDesc}>{uiText("调整对话消息流中的文字大小")}</p>
        <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
          {MSG_FONT_SIZES.map((s) => (
            <button
              key={s.id}
              onClick={() => handleFontSize(s.id)}
              style={{
                flex: 1,
                padding: '14px 0',
                borderRadius: 10,
                border: fontSize === s.id ? '2px solid var(--ink)' : '1.5px solid rgba(27,27,20,0.15)',
                background: fontSize === s.id ? 'var(--bg-paper-lt)' : 'transparent',
                boxShadow: fontSize === s.id ? '3px 3px 0 0 var(--ink)' : 'none',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column' as const,
                alignItems: 'center',
                gap: 6,
                transition: 'all 150ms',
              }}
            >
              <span style={{ fontSize: s.value, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.3 }}>Aa</span>
              <span style={{ fontSize: 12, color: fontSize === s.id ? 'var(--ink)' : 'rgba(27,27,20,0.5)', fontWeight: fontSize === s.id ? 600 : 400 }}>{uiText(s.label)}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function AuthTab({ token, onLogout }: { token: string | null; onLogout: () => void }) {
  const t = useT();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const clearSessions = useStore((s) => s.clearSessions);

  // Organization state
  const [orgs, setOrgs] = useState<OrgInfo[]>([]);
  const [activeOrgId, setActiveOrgId] = useState<string>('');
  const [orgLoading, setOrgLoading] = useState(false);
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetch('/auth/workspaces', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (data) {
          setOrgs(data.workspaces || []);
          setActiveOrgId(data.activeWorkspaceId || '');
        }
      })
      .catch(() => {});
  }, [token]);

  const handleSwitchOrg = async (orgId: string) => {
    if (orgId === activeOrgId || !token) return;
    setOrgLoading(true);
    try {
      const res = await fetch('/auth/workspace/switch', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ workspaceId: orgId }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.token) {
          localStorage.setItem('novax:token', data.token);
          window.location.reload();
        }
      }
    } catch { /* ignore */ }
    setOrgLoading(false);
  };

  const handleCreateOrg = async () => {
    const name = newOrgName.trim();
    if (!name || !token) return;
    setCreating(true);
    try {
      const res = await fetch('/auth/workspaces', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.workspace) {
          setOrgs((prev) => [...prev, { id: data.workspace.id, name: data.workspace.name, role: 'owner' }]);
          setNewOrgName('');
          setShowCreateOrg(false);
        }
      }
    } catch { /* ignore */ }
    setCreating(false);
  };

  const handleDeleteAccount = async () => {
    if (!token) return;
    setDeleting(true);
    try {
      const res = await fetch('/auth/delete-account', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        clearSessions();
        onLogout();
      }
    } catch { /* ignore */ }
    setDeleting(false);
  };

  return (
    <div className={styles.tabContent}>
      <span className={styles.sectionEyebrow}>{t('auth.eyebrow')}</span>
      <h2 className={styles.tabTitle}>{t('auth.title')}</h2>
      <p className={styles.tabDesc}>{t('auth.desc')}</p>

      {/* Organization switcher */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>{uiText("组织")}</h3>
        <p className={styles.sectionDesc}>{uiText("切换或创建组织，每个组织有独立的 Agent 和成员。")}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
          {orgs.map((org) => (
            <div
              key={org.id}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 14px', borderRadius: 8,
                border: org.id === activeOrgId ? '1.5px solid #5a6a2a' : '1px solid rgba(27,27,20,0.15)',
                background: org.id === activeOrgId ? 'rgba(90,106,42,0.08)' : 'transparent',
                cursor: org.id === activeOrgId ? 'default' : 'pointer',
                transition: 'all 150ms',
              }}
              onClick={() => handleSwitchOrg(org.id)}
            >
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{org.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                  {org.role === 'owner' ? t('team.owner') : t('team.member')}
                </div>
              </div>
              {org.id === activeOrgId ? (
                <span style={{ fontSize: 11, fontWeight: 600, color: '#5a6a2a', background: 'rgba(90,106,42,0.12)', padding: '3px 10px', borderRadius: 4 }}>{uiText("当前")}</span>
              ) : (
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{orgLoading ? uiText("切换中...") : uiText("切换")}</span>
              )}
            </div>
          ))}
          {showCreateOrg ? (
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <input
                style={{
                  flex: 1, padding: '8px 12px', fontSize: 13, borderRadius: 8,
                  border: '1px solid var(--border)', background: 'var(--bg-paper)', outline: 'none', color: 'var(--text-primary)',
                }}
                placeholder={uiText("组织名称")}
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreateOrg()}
                autoFocus
              />
              <button className={styles.saveBtn} onClick={handleCreateOrg} disabled={creating || !newOrgName.trim()}>
                {creating ? uiText("创建中...") : uiText("创建")}
              </button>
              <button className={styles.nameEditBtn} onClick={() => { setShowCreateOrg(false); setNewOrgName(''); }}>{uiText("取消")}</button>
            </div>
          ) : (
            <button
              onClick={() => setShowCreateOrg(true)}
              style={{
                padding: '8px 14px', fontSize: 12, fontWeight: 600, color: '#5a6a2a',
                background: 'none', border: '1px dashed rgba(90,106,42,0.4)', borderRadius: 8,
                cursor: 'pointer', marginTop: 4, transition: 'all 150ms',
              }}
            >
              {uiText("+ 创建新组织")}
            </button>
          )}
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('auth.signOut')}</h3>
        <p className={styles.sectionDesc}>{t('auth.signOutDesc')}</p>
        <button className={styles.dangerBtn} onClick={() => { clearSessions(); onLogout(); }}>
          {t('auth.signOut')}
        </button>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle} style={{ color: '#e34a2a' }}>{uiText("注销账号")}</h3>
        <p className={styles.sectionDesc}>{uiText("永久删除你的账号和所有数据，此操作不可撤销。")}</p>
        <button className={styles.dangerBtn} onClick={() => setConfirmDelete(true)}>
          {uiText("注销账号")}
        </button>
      </div>

      {confirmDelete && (
        <ConfirmModal
          title={uiText("确定要注销账号吗？")}
          desc={uiText("注销后你的账号、聊天记录和所有数据将被永久删除，无法恢复。")}
          confirmText={deleting ? uiText("注销中...") : uiText("确认注销")}
          onConfirm={handleDeleteAccount}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}

const GUIDE_SECTIONS = [
  { id: 'what-is-agent', label: '什么是 Agent' },
  { id: 'soul', label: 'Soul 人设指令' },
  { id: 'group-chat', label: '群聊与多 Agent 协作' },
  { id: 'tasks', label: '定时任务' },
  { id: 'webhook', label: 'Webhook 事件触发' },
  { id: 'skills', label: '技能系统' },
  { id: 'skill-market', label: 'Skill 市场' },
  { id: 'knowledge', label: '知识库' },
  { id: 'connections', label: '多平台连接' },
  { id: 'workspace', label: '组织与多租户' },
  { id: 'members', label: '成员与权限' },
];

function GuideTab() {
  const [activeSection, setActiveSection] = useState(GUIDE_SECTIONS[0].id);
  const bodyRef = useRef<HTMLDivElement>(null);

  const scrollTo = (id: string) => {
    setActiveSection(id);
    const el = bodyRef.current?.querySelector(`[data-section="${id}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const id = (entry.target as HTMLElement).dataset.section;
            if (id) setActiveSection(id);
          }
        }
      },
      { root: body, rootMargin: '-10% 0px -80% 0px', threshold: 0 },
    );
    body.querySelectorAll('[data-section]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className={styles.guideLayout}>
      {/* Left TOC */}
      <nav className={styles.guideToc}>
        <div className={styles.guideTocTitle}>{uiText("目录")}</div>
        {GUIDE_SECTIONS.map((s) => (
          <button
            key={s.id}
            className={`${styles.guideTocItem} ${activeSection === s.id ? styles.guideTocItemActive : ''}`}
            onClick={() => scrollTo(s.id)}
          >
            {uiText(s.label)}
          </button>
        ))}
      </nav>

      {/* Right content */}
      <div className={styles.guideBody} ref={bodyRef}>
        <div className={styles.guideContent}>

          {/* ── 什么是 Agent ── */}
          <h3 data-section="what-is-agent">{uiText("什么是 Agent")}</h3>
          <p>{uiText("Agent 是 NovaX 的核心概念。不同于传统的聊天机器人，每个 Agent 是一个拥有")}<strong>{uiText("独立身份、独立记忆、独立技能")}</strong>{uiText("的 AI 协作者。")}</p>
          <p>{uiText("你可以把 Agent 理解为团队中的一个虚拟成员。它可以：")}</p>
          <ul>
            <li>{uiText("拥有专属的")}<strong>{uiText("人设和行为规则")}</strong>{uiText("（由 Soul 指令定义）")}</li>
            <li>{uiText("记住与你的所有历史对话（")}<strong>{uiText("持久记忆")}</strong>）</li>
            <li>{uiText("安装不同的")}<strong>{uiText("技能模块")}</strong>{uiText("来扩展能力")}</li>
            <li>{uiText("通过")}<strong>{uiText("工作目录")}</strong>{uiText("在服务器上读写文件、执行代码")}</li>
            <li>{uiText("连接到飞书、Telegram、微信等")}<strong>{uiText("外部平台")}</strong></li>
          </ul>
          <p>{uiText("在「团队」页面中点击「新建 Agent」即可创建，创建后点击 Agent 卡片进入详情页进行配置。")}</p>

          <hr />

          {/* ── Soul 人设指令 ── */}
          <h3 data-section="soul">{uiText("Soul 人设指令")}</h3>
          <p>{uiText("Soul 是 Agent 最重要的配置项，它决定了 Agent 的角色定位、专长领域和行为方式。写好 Soul 是让 Agent 真正好用的关键。")}</p>

          <h4>{uiText("写 Soul 的要点")}</h4>
          <ul>
            <li><strong>{uiText("明确角色")}</strong> {uiText("— 告诉 Agent 它是谁，擅长什么领域。")}</li>
            <li><strong>{uiText("规定行为")}</strong> {uiText("— 回答时应该遵循什么格式、风格、步骤。")}</li>
            <li><strong>{uiText("设定边界")}</strong> {uiText("— 什么该做，什么不该做。")}</li>
          </ul>
          <p>{uiText("示例：")}</p>
          <pre style={{ background: 'rgba(27,27,20,0.05)', borderRadius: 8, padding: '12px 16px', fontSize: 12, lineHeight: 1.7, overflow: 'auto' }}>
{`你是一个资深的数据分析师，精通 SQL 和 Python。
- 用户提出分析需求时，先确认数据源和指标定义
- 输出结论时附带关键数据支撑
- 如果数据有异常，主动指出可能的原因
- 图表使用 matplotlib，配色统一用蓝绿色系`}
          </pre>
          <p>{uiText("不同的 Agent 可以有完全不同的 Soul。例如一个团队可以同时拥有「研究员」、「写手」、「代码审查员」等多个角色各异的 Agent。")}</p>

          <hr />

          {/* ── 群聊 ── */}
          <h3 data-section="group-chat">{uiText("群聊与多 Agent 协作")}</h3>
          <p>{uiText("群聊是 NovaX 的核心协作场景。在 Agent 列表顶部点击「全员群」即可进入。群聊中所有 Agent 和团队成员共处一个对话空间。")}</p>

          <h4>{uiText("@提及机制")}</h4>
          <p>{uiText("群聊中需要通过")} <code>@AgentName</code> {uiText("来指定哪个 Agent 回复。不带 @ 的消息不会触发任何 Agent。")}</p>

          <h4>{uiText("多 Agent 接力")}</h4>
          <p>{uiText("这是 NovaX 最强大的协作模式。你可以让多个 Agent 在同一个对话中接力完成复杂任务：")}</p>
          <ol>
            <li><code>{uiText("@研究员 搜索一下 2024 年新能源汽车出海市场数据")}</code></li>
            <li>{uiText("研究员输出数据后：")}<code>{uiText("@分析师 基于上面的数据，做一份竞争格局分析")}</code></li>
            <li>{uiText("分析完成后：")}<code>{uiText("@写手 把以上分析整理成一份 PDF 报告")}</code></li>
          </ol>
          <p>{uiText("每个 Agent 都能看到群聊中的上下文，所以后续 Agent 可以直接引用前面 Agent 的输出。")}</p>

          <hr />

          {/* ── 定时任务 ── */}
          <h3 data-section="tasks">{uiText("定时任务")}</h3>
          <p>{uiText("Agent 可以创建定时执行的后台任务，无需你手动触发。在对话界面顶部切换到「任务」标签页查看和管理。")}</p>

          <h4>{uiText("如何创建")}</h4>
          <p>{uiText("前提：Agent 需要安装")}<strong>「MetaSchedule」</strong>{uiText("技能。安装后直接在对话中用自然语言描述即可：")}</p>
          <ul>
            <li><code>{uiText("每天早上 9 点给我发一份行业新闻摘要")}</code></li>
            <li><code>{uiText("每周五下午 5 点生成本周工作总结")}</code></li>
            <li><code>{uiText("30 分钟后提醒我开会")}</code>{uiText("（一次性延时任务）")}</li>
          </ul>
          <p>{uiText("Agent 会自动创建 cron 任务。执行结果以消息形式出现在对话中。你可以在「任务」标签页中暂停、恢复或删除已创建的任务。")}</p>

          <hr />

          {/* ── Webhook ── */}
          <h3 data-section="webhook">{uiText("Webhook 事件触发")}</h3>
          <p>{uiText("Webhook 让外部系统通过 HTTP 请求触发 Agent 执行任务，适合与 CI/CD、监控告警、表单提交等系统对接。")}</p>

          <h4>{uiText("工作原理")}</h4>
          <ol>
            <li>{uiText("在「任务」标签页的「Webhook」子标签中创建，填写名称和提示词模板。")}</li>
            <li>{uiText("模板中用")} <code>{'{{payload}}'}</code> {uiText("占位，表示外部传入的数据。例如：")}</li>
          </ol>
          <pre style={{ background: 'rgba(27,27,20,0.05)', borderRadius: 8, padding: '12px 16px', fontSize: 12, lineHeight: 1.7, overflow: 'auto' }}>
{`收到部署通知，请分析并汇报关键信息：
{{payload}}`}
          </pre>
          <ol start={3}>
            <li>{uiText("创建后系统生成唯一 URL 和密钥，外部系统向该 URL 发送 POST 请求即可触发。")}</li>
          </ol>
          <p>{uiText("Agent 收到请求后自动执行，并将结果发送到对话中。")}</p>

          <hr />

          {/* ── 技能系统 ── */}
          <h3 data-section="skills">{uiText("技能系统")}</h3>
          <p>{uiText("技能是给 Agent 扩展能力的模块。不同 Agent 可以安装不同的技能组合，就像给员工配备不同的工具一样。")}</p>

          <h4>{uiText("安装方式")}</h4>
          <p>{uiText("在对话界面顶部切换到「技能」标签页，浏览可用列表并点击安装。")}</p>

          <h4>{uiText("核心内置技能")}</h4>
          <ul>
            <li><strong>MetaSchedule</strong> {uiText("— 让 Agent 具备创建和管理定时任务的能力。")}</li>
            <li><strong>NovaX Bus</strong> {uiText("— 允许 Agent 调用工作区内的其他 Agent，实现跨 Agent 的自动化流水线。例如 Agent A 完成分析后自动将结果转交给 Agent B 生成报告。")}</li>
            <li><strong>MetaSkill</strong> {uiText("— 让 Agent 自己创作新技能并发布到市场，供其他 Agent 安装使用。")}</li>
          </ul>

          <hr />

          {/* ── Skill 市场 ── */}
          <h3 data-section="skill-market">{uiText("Skill 市场")}</h3>
          <p>{uiText("在左侧导航栏的「Skill 市场」页面，可以浏览所有可用技能。")}</p>
          <ul>
            <li>{uiText("查看每个技能的功能说明、安装量和评分。")}</li>
            <li>{uiText("技能分为系统内置技能和社区发布的技能。")}</li>
            <li>{uiText("拥有 MetaSkill 技能的 Agent 可以根据你的需求自动创作新技能并发布。")}</li>
          </ul>

          <hr />

          {/* ── 知识库 ── */}
          <h3 data-section="knowledge">{uiText("知识库")}</h3>
          <p>{uiText("知识库是组织级别的共享文档库。上传的文档会被所有 Agent 在回答时自动参考，相当于给所有 Agent 统一配备了背景知识。")}</p>

          <h4>{uiText("使用方式")}</h4>
          <ol>
            <li>{uiText("进入左侧导航栏的「知识库」页面。")}</li>
            <li>{uiText("上传")} <code>.md</code> {uiText("格式文档，上传后立即生效。")}</li>
          </ol>

          <h4>{uiText("适合放入的内容")}</h4>
          <ul>
            <li>{uiText("团队规范、工作流程、审批标准")}</li>
            <li>{uiText("产品 PRD、技术架构文档")}</li>
            <li>{uiText("业务规则、行业术语、标准话术")}</li>
            <li>{uiText("常见问题和解答模板")}</li>
          </ul>

          <hr />

          {/* ── 多平台连接 ── */}
          <h3 data-section="connections">{uiText("多平台连接")}</h3>
          <p>{uiText("Agent 不仅可以在 NovaX 网页端使用，还可以连接到外部即时通讯平台。连接后 Agent 在所有平台共享同一套记忆、技能和知识库。")}</p>

          <p>{uiText("在对话界面顶部切换到「连接」标签页进行配置。目前支持：")}</p>
          <ul>
            <li><strong>{uiText("飞书 / Lark")}</strong> {uiText("— 在飞书开放平台创建应用，获取 App ID 和 App Secret 后接入。团队成员可在飞书中直接与 Agent 对话。")}</li>
            <li><strong>Telegram</strong> {uiText("— 通过 BotFather 创建 Bot，填入 Bot Token 即可连接。")}</li>
            <li><strong>{uiText("微信")}</strong> {uiText("— 通过 iLink 接入，实现在微信中与 Agent 交互。")}</li>
            <li><strong>Zoom</strong> {uiText("— 接入 Zoom 会议或聊天。")}</li>
          </ul>

          <hr />

          {/* ── 组织 ── */}
          <h3 data-section="workspace">{uiText("组织与多租户")}</h3>
          <p>{uiText("NovaX 采用组织（Workspace）为单位的多租户架构。每个组织是一个完全独立的协作空间，Agent、知识库、技能、成员、对话数据全部隔离。")}</p>
          <ul>
            <li>{uiText("每个用户最多可以创建")} <strong>{uiText("2 个组织")}</strong>{uiText("，同时可以被邀请加入他人的组织。")}</li>
            <li>{uiText("在「设置 → 账号管理」中创建新组织或切换组织。")}</li>
            <li>{uiText("切换组织后，你看到的 Agent、知识库、团队成员都会随之改变。")}</li>
          </ul>

          <hr />

          {/* ── 成员与权限 ── */}
          <h3 data-section="members">{uiText("成员与权限")}</h3>
          <p>{uiText("在「团队」页面管理组织成员。点击「邀请成员」输入邮箱即可发送邀请。")}</p>

          <h4>{uiText("三级权限体系")}</h4>
          <ul>
            <li><strong>{uiText("所有者（Owner）")}</strong> {uiText("— 拥有全部权限，管理组织设置、成员、Agent。每个组织只有一个所有者。")}</li>
            <li><strong>{uiText("管理员（Admin）")}</strong> {uiText("— 可以管理 Agent、知识库和成员，不能修改组织设置。")}</li>
            <li><strong>{uiText("成员（Member）")}</strong> {uiText("— 可以使用 Agent、参与对话、访问知识库，不能进行管理操作。")}</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function AboutTab() {
  const t = useT();
  const [openDoc, setOpenDoc] = useState<'guide' | 'agreement' | null>(null);

  return (
    <div className={styles.tabContent}>
      <span className={styles.sectionEyebrow}>{t('about.eyebrow')}</span>
      <h2 className={styles.tabTitle}>{t('about.title')}</h2>
      <p className={styles.tabDesc}>{t('about.desc')}</p>

      <div className={styles.aboutLogo}>
        <span className={styles.aboutLogoText}>
          <span className={styles.aboutLogoN}>N</span>
          <span className={styles.aboutLogoX}>X</span>
        </span>
      </div>
      <div className={styles.aboutName}>NovaX</div>
      <div className={styles.aboutVersion}>v1.0.0</div>
      <p className={styles.aboutDesc}>{t('about.description')}</p>

      <div className={styles.aboutLinks}>
        <button className={styles.aboutLinkBtn} onClick={() => setOpenDoc('guide')}>
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
          </svg>
          <div>
            <span className={styles.aboutLinkTitle}>{t('about.guideTitle')}</span>
            <span className={styles.aboutLinkDesc}>{t('about.guideDesc')}</span>
          </div>
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.aboutLinkArrow}>
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
        <button className={styles.aboutLinkBtn} onClick={() => setOpenDoc('agreement')}>
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
          </svg>
          <div>
            <span className={styles.aboutLinkTitle}>{t('about.agreementTitle')}</span>
            <span className={styles.aboutLinkDesc}>{t('about.agreementDesc')}</span>
          </div>
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.aboutLinkArrow}>
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>

      {openDoc && (
        <div className={styles.docOverlay} onClick={() => setOpenDoc(null)}>
          <div className={styles.docPanel} onClick={(e) => e.stopPropagation()}>
            <div className={styles.docHeader}>
              <h2 className={styles.docTitle}>
                {openDoc === 'guide' ? t('about.guideTitle') : t('about.agreementTitle')}
              </h2>
              <button className={styles.docClose} onClick={() => setOpenDoc(null)}>
                <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className={styles.docBody}>
              {openDoc === 'guide' ? <ProductGuide /> : <UserAgreement />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProductGuide() {
  const [lang] = useLang();
  if (lang === 'en') return <ProductGuideEN />;
  return <ProductGuideZH />;
}

function ProductGuideZH() {
  return (
    <div className={styles.guideContent}>
      <h3>{uiText("NovaX 是什么")}</h3>
      <p>{uiText("NovaX 是一个多 Agent AI 协作平台。你可以把它想象成一个 AI 团队工作台：在这里创建多个 AI Agent，每个 Agent 有自己的名字、人设、专长和工作空间。它们能读写文件、执行代码、搜索网络、管理任务，像真正的团队成员一样协同工作。")}</p>
      <p>{uiText("不同于普通的 AI 对话工具，NovaX 的 Agent 具备持久记忆、可安装技能、支持多人协作，并且可以连接飞书、Telegram、微信等即时通讯平台，让 AI 融入你的日常工作流。")}</p>

      <hr />

      <h3>{uiText("核心概念")}</h3>

      <h4>{uiText("Agent — 你的 AI 团队成员")}</h4>
      <p>{uiText("Agent 是 NovaX 的核心单元。每个 Agent 是一个独立的 AI 助手，拥有自己的：")}</p>
      <ul>
        <li><strong>{uiText("名称和头像")}</strong> {uiText("— 赋予 Agent 辨识度，方便在多 Agent 场景下区分。")}</li>
        <li><strong>{uiText("Soul（人设指令）")}</strong> {uiText("— 定义 Agent 的性格、专长和行为方式。比如你可以创建一个「代码审查员」Agent，让它专注于代码质量；再创建一个「产品经理」Agent，让它擅长需求分析。")}</li>
        <li><strong>{uiText("工作目录")}</strong> {uiText("— Agent 可以在指定的文件系统目录中读写文件，执行代码。不同 Agent 可以操作不同的项目目录。")}</li>
        <li><strong>{uiText("技能")}</strong> {uiText("— 通过安装不同的技能扩展 Agent 的能力边界。")}</li>
      </ul>

      <h4>{uiText("组织（Workspace）— 协作空间")}</h4>
      <p>{uiText("组织是一个独立的协作空间，类似于 Slack 的 Workspace 或飞书的团队。一个组织内的所有成员共享同一组 Agent 和知识库。你可以：")}</p>
      <ul>
        <li>{uiText("创建最多 2 个组织，分别用于不同的项目或团队。")}</li>
        <li>{uiText("通过邮箱邀请成员加入，设置不同的角色（所有者 / 管理员 / 成员）。")}</li>
        <li>{uiText("在不同组织之间切换，数据完全隔离。")}</li>
      </ul>

      <h4>{uiText("知识库（Memory）— 共享大脑")}</h4>
      <p>{uiText("知识库是组织级别的共享文档库。你上传的每一篇 Markdown 文档，都会成为该组织下所有 Agent 的共同知识。适合放入：")}</p>
      <ul>
        <li>{uiText("团队规范和工作流程")}</li>
        <li>{uiText("产品文档和技术架构")}</li>
        <li>{uiText("常见问题和标准回答")}</li>
        <li>{uiText("业务规则和约束条件")}</li>
      </ul>
      <p>{uiText("Agent 在回答问题时会自动参考这些文档，确保回答符合团队的规范和上下文。")}</p>

      <h4>{uiText("技能（Skill）— 能力扩展")}</h4>
      <p>{uiText("技能是可以安装到 Agent 上的功能模块。每个技能赋予 Agent 一项特定能力。NovaX 内置了一些核心技能，同时提供技能市场让你发现和安装更多功能：")}</p>
      <ul>
        <li><strong>{uiText("定时任务（MetaSchedule）")}</strong> {uiText("— 让 Agent 能够设定定时或周期性任务，比如每天早上生成报告。")}</li>
        <li><strong>{uiText("团队协作（NovaX Bus）")}</strong> {uiText("— 让 Agent 能够调用其他 Agent，实现跨 Agent 协作。")}</li>
        <li><strong>{uiText("技能创作（MetaSkill）")}</strong> {uiText("— 让 Agent 能够自动生成和发布新技能。")}</li>
      </ul>

      <hr />

      <h3>{uiText("快速上手")}</h3>

      <h4>{uiText("第一步：注册和登录")}</h4>
      <p>{uiText("使用邮箱注册账号。系统会发送验证码到你的邮箱，验证后设置密码即可完成注册。首次登录会自动创建一个默认组织。")}</p>

      <h4>{uiText("第二步：认识你的界面")}</h4>
      <p>{uiText("NovaX 的界面由左侧导航栏和右侧主内容区组成。导航栏提供五个主要入口：")}</p>
      <ul>
        <li><strong>{uiText("对话")}</strong> {uiText("— 与 Agent 交流的主界面，左侧是 Agent 列表，右侧是聊天窗口。")}</li>
        <li><strong>{uiText("知识库")}</strong> {uiText("— 管理共享文档，上传和浏览 .md 文件。")}</li>
        <li><strong>{uiText("团队")}</strong> {uiText("— 查看组织内的 Agent 和成员，创建新 Agent 或邀请新成员。")}</li>
        <li><strong>{uiText("Skill 市场")}</strong> {uiText("— 浏览和安装技能，管理已发布的技能。")}</li>
        <li><strong>{uiText("设置")}</strong> {uiText("— 个人偏好、组织管理、账号安全。")}</li>
      </ul>

      <h4>{uiText("第三步：创建你的第一个 Agent")}</h4>
      <p>{uiText("进入「团队」页面，点击「新建 Agent」按钮。填写 Agent 的名称和描述后创建。创建完成后，点击 Agent 卡片可以进入详情页，在这里你可以：")}</p>
      <ul>
        <li>{uiText("设置头像，从预设中选择或上传自定义图片。")}</li>
        <li>{uiText("编写 Soul —— 这是最关键的一步。Soul 决定了 Agent 如何理解任务和回答问题。写得越具体，Agent 的表现越好。")}</li>
      </ul>

      <h4>{uiText("第四步：开始对话")}</h4>
      <p>{uiText("进入「对话」页面，在左侧列表中点击一个 Agent 进入聊天。在输入框中发送消息，Agent 会实时流式返回回答。对话支持：")}</p>
      <ul>
        <li>{uiText("Markdown 格式渲染，包括代码高亮、表格、列表等。")}</li>
        <li>{uiText("Shift+Enter 换行，Enter 发送。")}</li>
        <li>{uiText("上传文件作为附件。")}</li>
        <li>{uiText("语音输入（需浏览器支持 Web Speech API）。")}</li>
      </ul>

      <h4>{uiText("第五步：丰富 Agent 的知识")}</h4>
      <p>{uiText("进入「知识库」页面，上传 .md 格式的文档。这些文档会成为所有 Agent 的共享参考资料。比如你可以上传公司的代码规范，之后让代码审查 Agent 按照这个规范来检查代码。")}</p>

      <hr />

      <h3>{uiText("功能详解")}</h3>

      <h4>{uiText("对话系统")}</h4>
      <p>{uiText("对话是 NovaX 的核心交互方式。每个 Agent 维护独立的对话历史。在对话界面中，顶部有三个标签页：")}</p>
      <ul>
        <li><strong>{uiText("消息")}</strong> {uiText("— 与 Agent 的聊天记录，支持实时流式输出。")}</li>
        <li><strong>{uiText("任务")}</strong> {uiText("— 查看 Agent 正在执行的后台任务，包括进度、状态和结果。")}</li>
        <li><strong>{uiText("技能")}</strong> {uiText("— 管理该 Agent 已安装的技能，或从市场安装新技能。")}</li>
      </ul>

      <h4>{uiText("群聊与多 Agent 协作")}</h4>
      <p>{uiText("在对话页面的 Agent 列表中，你会看到一个「全员群」入口。在群聊中，你可以用 @AgentName 的方式指定某个 Agent 来回答问题。这使得多个 Agent 可以在同一个对话中协作，比如先让「研究员」Agent 搜索资料，再让「写手」Agent 根据资料撰写文章。")}</p>
      <p>{uiText("没有 @ 任何 Agent 的消息会发送到群里但不会触发 Agent 回复，方便团队成员之间交流。")}</p>

      <h4>{uiText("技能市场")}</h4>
      <p>{uiText("技能市场分为两个维度：")}</p>
      <ul>
        <li><strong>{uiText("全局市场")}</strong> {uiText("— 在导航栏的「Skill 市场」中浏览所有可用技能，查看详情和安装说明。")}</li>
        <li><strong>{uiText("Agent 级别")}</strong> {uiText("— 在对话界面的「技能」标签中，为当前 Agent 安装或卸载技能。不同 Agent 可以安装不同的技能组合。")}</li>
      </ul>

      <h4>{uiText("组织与团队管理")}</h4>
      <p>{uiText("NovaX 支持多组织架构。在「设置 → 通用」中可以管理组织：")}</p>
      <ul>
        <li><strong>{uiText("创建组织")}</strong> {uiText("— 最多可创建 2 个组织。")}</li>
        <li><strong>{uiText("切换组织")}</strong> {uiText("— 点击即可切换到不同的组织，Agent 和知识库会随之改变。")}</li>
        <li><strong>{uiText("邀请成员")}</strong> {uiText("— 在「团队」页面通过邮箱邀请成员加入当前组织。")}</li>
        <li><strong>{uiText("角色权限")}</strong> {uiText("— 所有者拥有全部权限，管理员可管理 Agent 和成员，普通成员可以使用 Agent 和知识库。")}</li>
      </ul>

      <h4>{uiText("Codex 智能体底座")}</h4>
      <p>{uiText("NovaX 的所有 Agent 统一运行在 OpenAI Codex 上，默认使用")} <strong>gpt-5.6-sol</strong> {uiText("模型和")} <strong>high</strong> {uiText("推理强度，并在 workspace-write 沙箱内执行任务。")}</p>

      <h4>{uiText("多平台连接")}</h4>
      <p>{uiText("除了 Web 界面，NovaX 的 Agent 还可以连接到即时通讯平台：")}</p>
      <ul>
        <li><strong>{uiText("飞书 / Lark")}</strong> {uiText("— 通过飞书机器人直接在飞书中与 Agent 对话。")}</li>
        <li><strong>Telegram</strong> {uiText("— 通过 Telegram Bot 接入。")}</li>
        <li><strong>{uiText("微信")}</strong> {uiText("— 通过企业微信或 iLink 接入。")}</li>
      </ul>
      <p>{uiText("同一个 Agent 可以同时在 Web 和 IM 平台上工作，对话记忆和技能在所有平台间共享。")}</p>
    </div>
  );
}

function ProductGuideEN() {
  return (
    <div className={styles.guideContent}>
      <h3>What is NovaX</h3>
      <p>NovaX is a multi-agent AI collaboration platform. Think of it as an AI team workspace: create multiple AI agents, each with their own name, persona, expertise, and working directory. They can read and write files, execute code, search the web, and manage tasks — collaborating like real team members.</p>
      <p>Unlike typical AI chat tools, NovaX agents have persistent memory, installable skills, multi-user collaboration support, and can connect to messaging platforms like Feishu/Lark, Telegram, and WeChat, integrating AI into your daily workflow.</p>

      <hr />

      <h3>Core Concepts</h3>

      <h4>Agent — Your AI Team Member</h4>
      <p>Agents are the core unit of NovaX. Each agent is an independent AI assistant with its own:</p>
      <ul>
        <li><strong>Name and avatar</strong> — Gives each agent a distinct identity, making it easy to distinguish them in multi-agent scenarios.</li>
        <li><strong>Soul (persona instructions)</strong> — Defines the agent's personality, expertise, and behavior. For example, create a "Code Reviewer" agent focused on code quality, and a "Product Manager" agent skilled at requirements analysis.</li>
        <li><strong>Working directory</strong> — Agents can read and write files in a designated directory. Different agents can operate on different projects.</li>
        <li><strong>Skills</strong> — Extend an agent's capabilities by installing different skill modules.</li>
      </ul>

      <h4>Workspace — Collaboration Space</h4>
      <p>A workspace is an isolated collaboration space, similar to a Slack workspace or a Feishu team. All members within a workspace share the same agents and knowledge base. You can:</p>
      <ul>
        <li>Create up to 2 workspaces for different projects or teams.</li>
        <li>Invite members via email with different roles (Owner / Admin / Member).</li>
        <li>Switch between workspaces with complete data isolation.</li>
      </ul>

      <h4>Memory — Shared Brain</h4>
      <p>Memory is a workspace-level shared document library. Every Markdown document you upload becomes shared knowledge for all agents in that workspace. Ideal for:</p>
      <ul>
        <li>Team guidelines and workflows</li>
        <li>Product documentation and architecture</li>
        <li>FAQs and standard responses</li>
        <li>Business rules and constraints</li>
      </ul>
      <p>Agents automatically reference these documents when answering questions, ensuring responses align with team standards and context.</p>

      <h4>Skill — Capability Extensions</h4>
      <p>Skills are functional modules installable on agents. Each skill grants a specific capability. NovaX includes core built-in skills and provides a marketplace for discovering more:</p>
      <ul>
        <li><strong>Scheduled Tasks (MetaSchedule)</strong> — Enables agents to set up timed or recurring tasks, like generating daily reports.</li>
        <li><strong>Team Collaboration (NovaX Bus)</strong> — Enables agents to call other agents for cross-agent collaboration.</li>
        <li><strong>Skill Creation (MetaSkill)</strong> — Enables agents to automatically generate and publish new skills.</li>
      </ul>

      <hr />

      <h3>Getting Started</h3>

      <h4>Step 1: Register and Sign In</h4>
      <p>Register with your email. The system sends a verification code — verify and set a password to complete registration. Your first login automatically creates a default workspace.</p>

      <h4>Step 2: Know Your Interface</h4>
      <p>NovaX's interface consists of a left navigation bar and the main content area. The navigation provides five main sections:</p>
      <ul>
        <li><strong>Chat</strong> — The main interface for talking with agents. Agent list on the left, chat window on the right.</li>
        <li><strong>Memory</strong> — Manage shared documents, upload and browse .md files.</li>
        <li><strong>Contacts</strong> — View agents and members in your workspace, create new agents or invite members.</li>
        <li><strong>Skill Market</strong> — Browse and install skills, manage published skills.</li>
        <li><strong>Settings</strong> — Personal preferences, workspace management, account security.</li>
      </ul>

      <h4>Step 3: Create Your First Agent</h4>
      <p>Go to "Contacts" and click "New Agent". Fill in a name and description. After creation, click the agent card to access its detail page where you can:</p>
      <ul>
        <li>Set an avatar — choose from presets or upload a custom image.</li>
        <li>Write the Soul — this is the most important step. The Soul determines how the agent understands tasks and responds. The more specific you are, the better the agent performs.</li>
      </ul>

      <h4>Step 4: Start a Conversation</h4>
      <p>Go to "Chat", click an agent in the left list. Type a message and the agent responds in real-time with streaming output. Conversations support:</p>
      <ul>
        <li>Markdown rendering including code highlighting, tables, and lists.</li>
        <li>Shift+Enter for new line, Enter to send.</li>
        <li>File attachments.</li>
        <li>Voice input (requires browser Web Speech API support).</li>
      </ul>

      <h4>Step 5: Enrich Agent Knowledge</h4>
      <p>Go to "Memory" and upload .md documents. These become shared reference materials for all agents. For example, upload your company's coding standards, then have a code review agent check code against those standards.</p>

      <hr />

      <h3>Feature Details</h3>

      <h4>Chat System</h4>
      <p>Chat is the core interaction in NovaX. Each agent maintains independent conversation history. The chat interface has three tabs at the top:</p>
      <ul>
        <li><strong>Messages</strong> — Chat history with the agent, supporting real-time streaming output.</li>
        <li><strong>Tasks</strong> — View the agent's running background tasks, including progress, status, and results.</li>
        <li><strong>Skill</strong> — Manage installed skills for this agent, or install new ones from the marketplace.</li>
      </ul>

      <h4>Group Chat & Multi-Agent Collaboration</h4>
      <p>In the agent list on the Chat page, you'll see an "Everyone" group. In group chat, use @AgentName to direct a question to a specific agent. This enables multi-agent collaboration — for example, have a "Researcher" agent gather information, then have a "Writer" agent compose an article based on the findings.</p>
      <p>Messages without @mentions are sent to the group but won't trigger agent responses, allowing team members to communicate freely.</p>

      <h4>Skill Market</h4>
      <p>The skill system works on two levels:</p>
      <ul>
        <li><strong>Global Market</strong> — Browse all available skills in the "Skill Market" navigation tab, view details and installation instructions.</li>
        <li><strong>Agent Level</strong> — In the chat "Skill" tab, install or uninstall skills for the current agent. Different agents can have different skill combinations.</li>
      </ul>

      <h4>Workspace & Team Management</h4>
      <p>NovaX supports a multi-workspace architecture. Manage workspaces in "Settings &rarr; General":</p>
      <ul>
        <li><strong>Create workspace</strong> — Up to 2 workspaces allowed.</li>
        <li><strong>Switch workspace</strong> — Click to switch; agents and knowledge base change accordingly.</li>
        <li><strong>Invite members</strong> — Invite via email on the "Contacts" page.</li>
        <li><strong>Role permissions</strong> — Owners have full control, Admins can manage agents and members, Members can use agents and knowledge base.</li>
      </ul>

      <h4>Codex Agent Runtime</h4>
      <p>Every NovaX agent runs on OpenAI Codex. The platform defaults to <strong>gpt-5.6-sol</strong> with <strong>high</strong> reasoning effort and executes tasks in a workspace-write sandbox.</p>

      <h4>Multi-Platform Connectivity</h4>
      <p>Beyond the web interface, NovaX agents can connect to messaging platforms:</p>
      <ul>
        <li><strong>Feishu / Lark</strong> — Chat with agents directly in Feishu via bot integration.</li>
        <li><strong>Telegram</strong> — Connect via Telegram Bot.</li>
        <li><strong>WeChat</strong> — Connect via enterprise WeChat or iLink.</li>
      </ul>
      <p>The same agent can work on both Web and IM platforms simultaneously. Conversation memory and skills are shared across all platforms.</p>
    </div>
  );
}

function UserAgreement() {
  const [lang] = useLang();
  if (lang === 'en') return <UserAgreementEN />;
  if (lang === 'zhTW') return <UserAgreementZHTW />;
  return <UserAgreementZH />;
}

function UserAgreementZH() {
  return (
    <div className={styles.guideContent}>
      <p className={styles.agreementMeta}>{uiText("最后更新：2026 年 5 月")}</p>

      <h4>{uiText("1. 产品性质")}</h4>
      <p>{uiText("NovaX 是一个")}<strong>{uiText("个人练手的技术演示项目（Demo）")}</strong>{uiText("，仅用于展示多 Agent AI 协作平台的技术可行性。本产品")}<strong>{uiText("不会用于商业化运营")}</strong>{uiText("，不以盈利为目的，不会向用户收取任何费用，也不会以任何形式进行商业推广或销售。")}</p>

      <h4>{uiText("2. 数据与隐私")}</h4>
      <ul>
        <li>{uiText("我们")}<strong>{uiText("不会主动收集")}</strong>{uiText("用户的个人信息用于任何商业用途。")}</li>
        <li>{uiText("你在使用过程中输入的对话内容仅用于与 AI 引擎交互，不会被额外存储或用于训练模型。")}</li>
        <li>{uiText("注册所需的邮箱仅用于账号验证和登录，不会用于营销或转让给第三方。")}</li>
        <li>{uiText("知识库中上传的文档存储在服务器本地，仅供你所在组织的 Agent 读取。")}</li>
        <li>{uiText("你可以随时在设置中注销账号，删除所有关联数据。")}</li>
      </ul>

      <h4>{uiText("3. 第三方服务")}</h4>
      <p>{uiText("本产品调用 OpenAI Codex 服务处理对话请求。你的对话内容会按照该服务的隐私政策进行处理。建议避免在对话中输入敏感个人信息。")}</p>

      <h4>{uiText("4. 服务可用性")}</h4>
      <p>{uiText("作为个人练手 Demo 项目，我们")}<strong>{uiText("不保证")}</strong>{uiText("服务的持续可用性、稳定性或数据持久性。服务可能随时暂停、变更或终止，恕不另行通知。请勿将本产品用于生产环境或存放重要数据。")}</p>

      <h4>{uiText("5. 免责声明")}</h4>
      <ul>
        <li>{uiText("AI 生成的内容可能包含错误或不准确信息，请自行判断和验证。")}</li>
        <li>{uiText("因使用本产品产生的任何直接或间接损失，开发者不承担责任。")}</li>
        <li>{uiText("本产品不适用于医疗建议、法律咨询、金融决策等专业场景。")}</li>
      </ul>

      <h4>{uiText("6. 联系方式")}</h4>
      <p>{uiText("如有任何问题或疑虑，请通过产品内的反馈渠道联系开发者。")}</p>
    </div>
  );
}

function UserAgreementZHTW() {
  return (
    <div className={styles.guideContent}>
      <p className={styles.agreementMeta}>{uiText("最後更新：2026 年 5 月")}</p>

      <h4>{uiText("1. 產品性質")}</h4>
      <p>{uiText("NovaX 是一個")}<strong>{uiText("個人練手的技術展示專案（Demo）")}</strong>{uiText("，僅用於展示多 Agent AI 協作平台的技術可行性。本產品")}<strong>{uiText("不會用於商業化營運")}</strong>{uiText("，不以營利為目的，不會向使用者收取任何費用，也不會以任何形式進行商業推廣或銷售。")}</p>

      <h4>{uiText("2. 資料與隱私")}</h4>
      <ul>
        <li>{uiText("我們")}<strong>{uiText("不會主動收集")}</strong>{uiText("使用者的個人資訊用於任何商業用途。")}</li>
        <li>{uiText("你在使用過程中輸入的對話內容僅用於與 AI 引擎互動，不會被額外儲存或用於訓練模型。")}</li>
        <li>{uiText("註冊所需的信箱僅用於帳號驗證和登入，不會用於行銷或轉讓給第三方。")}</li>
        <li>{uiText("知識庫中上傳的文件儲存在伺服器本地，僅供你所在組織的 Agent 讀取。")}</li>
        <li>{uiText("你可以隨時在設定中登出帳號，刪除所有關聯資料。")}</li>
      </ul>

      <h4>{uiText("3. 第三方服務")}</h4>
      <p>{uiText("本產品呼叫 OpenAI Codex 服務處理對話請求。你的對話內容會按照該服務的隱私政策進行處理。建議避免在對話中輸入敏感個人資訊。")}</p>

      <h4>{uiText("4. 服務可用性")}</h4>
      <p>{uiText("作為個人練手 Demo 專案，我們")}<strong>{uiText("不保證")}</strong>{uiText("服務的持續可用性、穩定性或資料持久性。服務可能隨時暫停、變更或終止，恕不另行通知。請勿將本產品用於生產環境或存放重要資料。")}</p>

      <h4>{uiText("5. 免責聲明")}</h4>
      <ul>
        <li>{uiText("AI 產生的內容可能包含錯誤或不準確資訊，請自行判斷和驗證。")}</li>
        <li>{uiText("因使用本產品產生的任何直接或間接損失，開發者不承擔責任。")}</li>
        <li>{uiText("本產品不適用於醫療建議、法律諮詢、金融決策等專業場景。")}</li>
      </ul>

      <h4>{uiText("6. 聯絡方式")}</h4>
      <p>{uiText("如有任何問題或疑慮，請透過產品內的回饋管道聯絡開發者。")}</p>
    </div>
  );
}

function UserAgreementEN() {
  return (
    <div className={styles.guideContent}>
      <p className={styles.agreementMeta}>Last updated: May 2026</p>

      <h4>1. Nature of the Product</h4>
      <p>NovaX is a <strong>personal hobby / learning project (Demo)</strong>, created solely to showcase the technical feasibility of a multi-agent AI collaboration platform. This product <strong>will not be commercialized</strong> in any form — it is not intended for profit, will not charge users any fees, and will not be marketed or sold.</p>

      <h4>2. Data & Privacy</h4>
      <ul>
        <li>We <strong>do not actively collect</strong> personal information for any commercial purposes.</li>
        <li>Conversations you have with agents are used only for AI engine interaction and are not additionally stored or used for model training.</li>
        <li>Your registration email is used solely for account verification and login. It will not be used for marketing or shared with third parties.</li>
        <li>Documents uploaded to the knowledge base are stored locally on the server and accessible only to agents within your workspace.</li>
        <li>You may delete your account and all associated data at any time from Settings.</li>
      </ul>

      <h4>3. Third-Party Services</h4>
      <p>This product uses OpenAI Codex to process conversations. Your messages are handled according to that service's privacy policy. Avoid sharing sensitive personal information in conversations.</p>

      <h4>4. Service Availability</h4>
      <p>As a personal demo project, we <strong>do not guarantee</strong> continuous availability, stability, or data persistence. The service may be suspended, modified, or terminated at any time without notice. Do not use this product in production environments or to store critical data.</p>

      <h4>5. Disclaimer</h4>
      <ul>
        <li>AI-generated content may contain errors or inaccuracies. Please verify independently.</li>
        <li>The developer assumes no liability for any direct or indirect losses arising from the use of this product.</li>
        <li>This product is not suitable for medical advice, legal consultation, financial decisions, or other professional use cases.</li>
      </ul>

      <h4>6. Contact</h4>
      <p>For any questions or concerns, please reach out through the in-app feedback channel.</p>
    </div>
  );
}
